import { API_BASE_URL } from '../constants';

class OperatorService {
  /**
   * Query the COMPLIANCE OPERATOR
   * @param {string} queryText - The compliance question
   * @param {Array<string>} frameworks - Optional framework filter (e.g., ["MiCA", "DORA"])
   * @returns {Promise<{answer: string, sources: Array}>}
   */
  async query(queryText, frameworks = null) {
    try {
      const payload = { query: queryText };
      if (frameworks && frameworks.length > 0) {
        payload.frameworks = frameworks;
      }

      const response = await fetch(`${API_BASE_URL}/compliance-operator/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'OPERATOR REQUEST FAILED');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('COMPLIANCE OPERATOR Error:', error);
      throw error;
    }
  }

  /**
   * Get OPERATOR system status
   * @returns {Promise<{model: string, documentCount: number, lastUpdate: string, avgLatency: number}>}
   */
  async getStatus() {
    try {
      const response = await fetch(`${API_BASE_URL}/compliance-operator/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      if (!response.ok) {
        // Return default status if endpoint not available yet
        return {
          model: 'Gemini 2.5 Flash (Vertex AI)',
          documentCount: 47234,
          lastUpdate: new Date().toISOString(),
          avgLatency: 0.3
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Status request error:', error);
      // Return default status on error
      return {
        model: 'Gemini 2.5 Flash (Vertex AI)',
        documentCount: 47234,
        lastUpdate: new Date().toISOString(),
        avgLatency: 0.3
      };
    }
  }

  /**
   * Get available regulatory frameworks
   * @returns {Promise<{frameworks: Array}>}
   */
  async getFrameworks() {
    try {
      const response = await fetch(`${API_BASE_URL}/compliance-operator/frameworks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      if (!response.ok) {
        // Return default frameworks if endpoint not available
        return {
          frameworks: [
            { code: 'MiCA', name: 'Markets in Crypto-Assets Regulation', color: '#0078d4' },
            { code: 'DORA', name: 'Digital Operational Resilience Act', color: '#107c10' },
            { code: 'GDPR', name: 'General Data Protection Regulation', color: '#5c2d91' },
            { code: 'AML', name: 'Anti-Money Laundering Directive (6AMLD)', color: '#7a6000' },
            { code: 'MiFID_II', name: 'Markets in Financial Instruments Directive II', color: '#0078d4' },
            { code: 'NIS2', name: 'Network and Information Security Directive 2', color: '#a4262c' }
          ]
        };
      }

      return await response.json();
    } catch (error) {
      console.error('Frameworks request error:', error);
      return { frameworks: [] };
    }
  }

  /**
   * Save query to Vault
   * @param {Object} queryData - Query data to save
   * @returns {Promise<{vault_id: string, success: boolean}>}
   */
  async saveToVault(queryData) {
    try {
      const response = await fetch(`${API_BASE_URL}/vault/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify(queryData)
      });

      if (!response.ok) {
        throw new Error('Failed to save to vault');
      }

      return await response.json();
    } catch (error) {
      console.error('Save to vault error:', error);
      throw error;
    }
  }

  /**
   * Get vault items for current user
   * @param {Object} filters - Optional filters (starred_only, frameworks, tags)
   * @returns {Promise<{items: Array, count: number}>}
   */
  async getVaultItems(filters = {}) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const params = new URLSearchParams({
        user_id: user.id || 'demo-user',
        company_id: user.company_id || 'demo-company',
        ...filters
      });

      const response = await fetch(`${API_BASE_URL}/vault?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      if (!response.ok) {
        return { items: [], count: 0 };
      }

      return await response.json();
    } catch (error) {
      console.error('Get vault items error:', error);
      return { items: [], count: 0 };
    }
  }

  /**
   * Update vault item
   * @param {string} vaultId - Vault item ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<{success: boolean}>}
   */
  async updateVaultItem(vaultId, updates) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${API_BASE_URL}/vault/${vaultId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({
          user_id: user.id || 'demo-user',
          ...updates
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update vault item');
      }

      return await response.json();
    } catch (error) {
      console.error('Update vault item error:', error);
      throw error;
    }
  }

  /**
   * Delete vault item
   * @param {string} vaultId - Vault item ID
   * @returns {Promise<{success: boolean}>}
   */
  async deleteVaultItem(vaultId) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${API_BASE_URL}/vault/${vaultId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({
          user_id: user.id || 'demo-user'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete vault item');
      }

      return await response.json();
    } catch (error) {
      console.error('Delete vault item error:', error);
      throw error;
    }
  }

  /**
   * Upload document to vault
   * @param {FormData} formData - FormData with document file and metadata
   * @returns {Promise<{document_id: string, success: boolean}>}
   */
  async uploadDocument(formData) {
    try {
      const response = await fetch(`${API_BASE_URL}/vault/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload document');
      }

      return await response.json();
    } catch (error) {
      console.error('Upload document error:', error);
      throw error;
    }
  }
}

export default new OperatorService();
